import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Typography
} from '@mui/material';
import type { Usuario } from '../types/usuario.types';
import { usuarioService } from '../services/usuarioService';
import { rolService } from '../services/rolService';
import { useUsuarioStore } from '../store/usuarioStore';

interface AsignarRolesDialogProps {
  open: boolean;
  onClose: () => void;
  usuario?: Usuario;
  onSuccess: () => void;
}

export const AsignarRolesDialog = ({ open, onClose, usuario, onSuccess }: AsignarRolesDialogProps) => {
  const { roles, setRoles } = useUsuarioStore();
  const [selectedRoles, setSelectedRoles] = useState<number[]>([]);

  useEffect(() => {
    if (open) {
      loadRoles();
    }
  }, [open]);

  useEffect(() => {
    if (usuario) {
      loadUsuarioRoles();
    }
  }, [usuario]);

  const loadRoles = async () => {
    try {
      const data = await rolService.getAll();
      setRoles(data);
    } catch (error) {
      console.error('Error al cargar roles:', error);
    }
  };

  const loadUsuarioRoles = async () => {
    if (!usuario) return;

    try {
      const rolesUsuario = await usuarioService.getRoles(usuario.id);
      setSelectedRoles(rolesUsuario.map(r => r.id));
    } catch (error) {
      console.error('Error al cargar roles del usuario:', error);
    }
  };

  const handleToggle = (rolId: number) => {
    setSelectedRoles(prev =>
      prev.includes(rolId)
        ? prev.filter(id => id !== rolId)
        : [...prev, rolId]
    );
  };

  const handleSubmit = async () => {
    if (!usuario) return;

    try {
      await usuarioService.asignarRoles(usuario.id, selectedRoles);
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error al asignar roles:', error);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Asignar Roles - {usuario?.userName}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Selecciona los roles que deseas asignar a este usuario
        </Typography>
        <FormGroup>
          {roles.map(rol => (
            <FormControlLabel
              key={rol.id}
              control={
                <Checkbox
                  checked={selectedRoles.includes(rol.id)}
                  onChange={() => handleToggle(rol.id)}
                />
              }
              label={`${rol.descripcion} (${rol.codigo})`}
            />
          ))}
        </FormGroup>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button onClick={handleSubmit} variant="contained">
          Guardar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AsignarRolesDialog;
